import { ArrowLeft, Gamepad2, LogOut, Radio } from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import type { Game } from "../../data/games";
import {
  getGameDefinition,
  type GameDefinitionResult,
} from "../../game-engine/gameDefinitions";
import type { GameDefinition } from "../../game-engine/types";
import { useGameSession } from "../../hooks/useGameSession";
import { HOST_MODE } from "../../net/mode";
import { ConfirmExitDialog } from "../ConfirmExitDialog/ConfirmExitDialog";
import { GameLobby } from "../GameLobby/GameLobby";
import { GameRound } from "../GameRound/GameRound";
import { GameSummary } from "../GameSummary/GameSummary";
import styles from "./GameSession.module.css";

interface GameSessionProps {
  game: Game;
  onExit: () => void;
}

interface ReadyGameSessionProps extends GameSessionProps {
  definition: GameDefinition;
}

function ConfigurationError({
  game,
  result,
  onExit,
}: {
  game: Game;
  result: Extract<GameDefinitionResult, { status: "invalid" }>;
  onExit: () => void;
}) {
  return (
    <main className={styles.configurationError}>
      <span className={styles.errorIcon} aria-hidden="true">
        <Gamepad2 />
      </span>
      <h1>Spelet kunde inte laddas</h1>
      <p>
        JSON-definitionen för {game.title} behöver rättas innan simuleringen
        kan starta.
      </p>
      <ul>
        {result.errors.map((error) => (
          <li key={error}>{error}</li>
        ))}
      </ul>
      <button type="button" onClick={onExit}>
        <ArrowLeft aria-hidden="true" />
        Till startsidan
      </button>
    </main>
  );
}

function ReadyGameSession({
  game,
  definition,
  onExit,
}: ReadyGameSessionProps) {
  const {
    state,
    currentRound,
    currentResult,
    roundParticipants,
    roundCount,
    countdown,
    joinAll,
    removeParticipant,
    startGame,
    answerAsHost,
    nextRound,
    resetSession,
  } = useGameSession(definition, game, HOST_MODE);
  const isLive = HOST_MODE === "live";
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const exitButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (state.phase === "answering") {
      return;
    }

    const animationFrame = requestAnimationFrame(() => {
      contentRef.current
        ?.querySelector<HTMLElement>("[data-session-heading]")
        ?.focus();
    });

    return () => cancelAnimationFrame(animationFrame);
  }, [state.currentRoundIndex, state.phase, state.runId]);

  const closeExitConfirmation = useCallback(() => {
    setShowExitConfirmation(false);
    requestAnimationFrame(() => exitButtonRef.current?.focus());
  }, []);

  const requestExit = () => {
    if (state.phase === "lobby" || state.phase === "summary") {
      onExit();
    } else {
      setShowExitConfirmation(true);
    }
  };

  return (
    <div className={styles.session}>
      <div
        className={styles.sessionContent}
        ref={contentRef}
        inert={showExitConfirmation}
        aria-hidden={showExitConfirmation ? "true" : undefined}
      >
        <header className={styles.topBar}>
          <span className={styles.brand}>
            <span aria-hidden="true">
              <Radio />
            </span>
            PARTYCAST
          </span>
          <span className={styles.sessionName}>{game.title}</span>
          <span className={styles.sessionCode}>
            <small>Lobby</small>
            {state.code}
          </span>
          <button
            ref={exitButtonRef}
            type="button"
            aria-label={
              state.phase === "lobby" ? "Tillbaka till startsidan" : "Avsluta spel"
            }
            onClick={requestExit}
          >
            {state.phase === "lobby" ? (
              <ArrowLeft aria-hidden="true" />
            ) : (
              <LogOut aria-hidden="true" />
            )}
          </button>
        </header>

        {state.phase === "lobby" ? (
          <GameLobby
            game={game}
            code={state.code}
            participants={state.participants}
            onJoinAll={joinAll}
            onParticipantLeave={removeParticipant}
            onStart={startGame}
            hideJoinAll={isLive}
          />
        ) : state.phase === "countdown" ? (
          <main className={styles.countdown}>
            <span className={styles.countdownEyebrow}>Get ready</span>
            <h1 data-session-heading tabIndex={-1} className={styles.countdownNumber}>
              {countdown > 0 ? countdown : "Kör!"}
            </h1>
            <p className={styles.countdownHint}>Nästa fråga kommer strax…</p>
          </main>
        ) : state.phase === "summary" ? (
          <GameSummary
            definition={definition}
            participants={roundParticipants}
            results={state.results}
            onReplay={resetSession}
            onExit={onExit}
          />
        ) : currentRound ? (
          <GameRound
            round={currentRound}
            result={currentResult}
            phase={state.phase}
            participants={roundParticipants}
            answeredParticipantIds={state.answeredParticipantIds}
            hostAnswerId={state.hostAnswerId}
            roundIndex={state.currentRoundIndex}
            roundCount={roundCount}
            onHostAnswer={answerAsHost}
            onNext={nextRound}
          />
        ) : (
          <ConfigurationError
            game={game}
            result={{
              status: "invalid",
              errors: ["Den aktuella rundan saknas."],
            }}
            onExit={onExit}
          />
        )}
      </div>

      {showExitConfirmation ? (
        <ConfirmExitDialog
          onCancel={closeExitConfirmation}
          onConfirm={onExit}
        />
      ) : null}
    </div>
  );
}

export function GameSession({ game, onExit }: GameSessionProps) {
  const result = getGameDefinition(game.id);

  if (result.status === "invalid") {
    return <ConfigurationError game={game} result={result} onExit={onExit} />;
  }

  if (result.status === "unsupported") {
    return null;
  }

  return (
    <ReadyGameSession
      game={game}
      definition={result.definition}
      onExit={onExit}
    />
  );
}
